/* Vocabulário canônico de status de recurso.
 *
 * Antes desta tabela, cada feature declarava a sua em `constants/status.ts` — e
 * a mesma chave rendia textos diferentes por tela: `nova` era "Nova" em
 * jornadas, "Disponíveis" no filtro de quizzes e "Disponível" em prêmios. É o
 * que fazia /quizzes e /jornadas parecerem produtos distintos.
 *
 * A chave de disponibilidade chama-se `available`, não `nova`. O nome antigo
 * carregava duas ideias — "liberado" e "recém-publicado" — e a segunda agora
 * mora em `shared/lib/recency`, como eixo independente. */

export type ResourceStatus =
  | 'available' // liberado, ainda não iniciado
  | 'progress'
  | 'success'
  | 'locked'
  | 'expired'
  | 'danger'

export const RESOURCE_STATUSES = [
  'available',
  'progress',
  'success',
  'locked',
  'expired',
  'danger'
] as const satisfies readonly ResourceStatus[]

export type StatusLabels = Record<ResourceStatus, string>
export type StatusOrder = Record<ResourceStatus, number>

/* Forma masculina. Features de gênero feminino (jornadas, missões) declaram o
   override — repetição pequena e explícita, preferível a um motor de flexão. */
export const STATUS_LABELS: StatusLabels = {
  available: 'Disponível',
  progress: 'Em andamento',
  success: 'Concluído',
  locked: 'Bloqueado',
  expired: 'Encerrado',
  danger: 'Perda'
}

/** Rótulo de chip de filtro — plural, porque nomeia um conjunto. */
export const STATUS_FILTER_LABELS: StatusLabels = {
  available: 'Disponíveis',
  progress: 'Em andamento',
  success: 'Concluídos',
  locked: 'Bloqueados',
  expired: 'Encerrados',
  danger: 'Perdas'
}

/* Ordem única: vale para ordenar itens dentro de um filtro E para ordenar as
   seções da visão "Todas". Sendo a mesma fonte, as duas não podem divergir. */
export const STATUS_ORDER: StatusOrder = {
  progress: 0,
  available: 1,
  locked: 2,
  success: 3,
  expired: 4,
  danger: 5
}

/**
 * Sobrescreve rótulos pontuais preservando o resto da tabela.
 *
 * A feature declara só o delta — `overrideLabels({ success: 'Ganhei' })` — em
 * vez de recopiar as seis chaves. Uma tabela recopiada envelhece em silêncio;
 * um delta de uma linha fica auditável.
 */
export const overrideLabels = (
  overrides: Partial<StatusLabels>,
  base: StatusLabels = STATUS_LABELS
): StatusLabels => ({ ...base, ...overrides })

/**
 * Mesma ideia para a ordem. Prêmios exibem "Ganhos" primeiro, ao contrário do
 * canônico: `resolveOrder({ success: -1 })` deixa essa divergência visível em
 * uma linha, em vez de escondida numa segunda tabela completa.
 */
export const resolveOrder = (
  overrides: Partial<StatusOrder> = {}
): StatusOrder => ({ ...STATUS_ORDER, ...overrides })
